/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  TelemetryAction,
  TelemetryComponent,
  TelemetryState,
  useSendTelemetry,
} from '../../../hooks/useSendTelemetry';
import { Flex, Image, List, ListItem, Text } from '@getgo/chameleon-chakra-ui';
import { Link } from '../../../components/Link/Link';
import { isMac, isFirefox } from '../../../services/ClientService';
import { isInstantJoinTechnicallySupported } from '../../../services/ProductCompatibilityService';
interface Props {
  instantJoinUrl?: string;
  nativeDownloadUrl: string;
}

const messages = defineMessages({
  instantJoinLink: {
    id: 'downloadInstructions.troubleshooting.instantJoinLink',
  },
  ariaTroubleshooting: {
    id: 'downloadInstructions.troubleshooting.ariaHeading',
  },
  ariaInstructions: {
    id: 'downloadInstructions.installInstructions.ariaHeading',
  },
  step1default: {
    id: 'downloadInstructions.installInstructions.step1.default',
  },
  step1firefox: {
    id: 'downloadInstructions.installInstructions.step1.firefox',
  },
  step2MacOnly: {
    id: 'downloadInstructions.installInstructions.step2MacOnly',
  },
  step3: {
    id: 'downloadInstructions.installInstructions.step3',
  },
  heading: {
    id: 'downloadInstructions.heading',
  },
  installInstructions: {
    id: 'downloadInstructions.installInstructions.heading',
  },
  troubleshootingHeading: {
    id: 'downloadInstructions.troubleshooting.heading',
  },
  tryAgainLink: {
    id: 'downloadInstructions.troubleshooting.tryAgainLink',
  },
  tryAgainWithInstantJoin: {
    id: 'downloadInstructions.troubleshooting.tryAgainWithInstantJoin',
  },
});

export const step1automationId = 'step1automationId';

const TroubleshootingLinks: React.FC<Props> = (props) => {
  const sendTelemetry = useSendTelemetry();
  const { formatMessage: f } = useIntl();

  if (isInstantJoinTechnicallySupported && props.instantJoinUrl) {
    return (
      <Flex>
        <Link
          textDecoration='underline'
          href={props.nativeDownloadUrl}
          onClick={() =>
            sendTelemetry(
              TelemetryComponent.Native,
              TelemetryAction.Download,
              TelemetryState.DownloadInstructions
            )
          }
        >
          {f(messages.tryAgainLink)}
        </Link>
        <Text mx={1}>{f(messages.tryAgainWithInstantJoin)}</Text>
        <Link
          textDecoration='underline'
          href={props.instantJoinUrl}
          onClick={() =>
            sendTelemetry(
              TelemetryComponent.Web,
              TelemetryAction.Join,
              TelemetryState.DownloadInstructions
            )
          }
        >
          {f(messages.instantJoinLink)}
        </Link>
        .
      </Flex>
    );
  } else {
    return (
      <Link
        textDecoration='underline'
        href={props.nativeDownloadUrl}
        onClick={() =>
          sendTelemetry(
            TelemetryComponent.Native,
            TelemetryAction.Download,
            TelemetryState.DownloadInstructions
          )
        }
      >
        {f(messages.tryAgainLink)}
      </Link>
    );
  }
};

function osInstallInstructions() {
  const macDownloadFileName = 'GoTo Opener.dmg';
  const windowsDownloadFileName = 'GoToMeeting Opener.exe';
  const downloadFileName = () =>
    isMac ? macDownloadFileName : windowsDownloadFileName;

  return (
    <List as='ol' ml={isMac ? 10 : 0} styleType='decimal'>
      <ListItem lineHeight='1.75' data-testid={step1automationId}>
        <FormattedMessage
          {...(isFirefox ? messages.step1firefox : messages.step1default)}
          values={{
            appFileName: (
              <span style={{ fontWeight: 'bold' }}>{downloadFileName()}</span>
            ),
          }}
        />
      </ListItem>
      {isMac && (
        <ListItem>
          <FormattedMessage {...messages.step2MacOnly} />
        </ListItem>
      )}
      <ListItem>
        <FormattedMessage {...messages.step3} />
      </ListItem>
    </List>
  );
}

export const DownloadInstructions: React.FC<Props> = (props) => {
  const { formatMessage: f } = useIntl();
  return (
    <Flex
      direction='column'
      justifyContent='center'
      alignItems='center'
      id='downloadInstructions'
      pb='10vh'
      role='presentation'
    >
      <Text as='h1'>{f(messages.heading)}</Text>
      <Image
        my='1.875rem'
        src={`${process.env.PUBLIC_URL}/static/media/desktop-icon.svg`}
        alt='GoToMeeting Logo'
        aria-hidden='true'
      />
      <Flex
        direction='column'
        alignItems='center'
        aria-label={f(messages.ariaInstructions)}
      >
        <Text mt={4}>{f(messages.installInstructions)}</Text>
        <Flex mt={4}>{osInstallInstructions()}</Flex>
      </Flex>
      <Flex
        direction='column'
        alignItems='center'
        mt='3.125rem'
        aria-label={f(messages.ariaTroubleshooting)}
      >
        <Text>{f(messages.troubleshootingHeading)}</Text>
        <TroubleshootingLinks {...props} />
      </Flex>
    </Flex>
  );
};
